import { useMemo, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';
import { ProductGroupLeadTime } from '@texas/api/endpoints/statisticsApi';
import { useTranslation } from 'react-i18next';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';
import { PaOverviewModal } from '../shared/components/productApprovals/PaOverviewModal';
import { PaTableColumns } from '@texas/components/dashboard/PaTableColumns';
import { FilterOptionType } from '@texas/components/filter/types/types';
import { defaultEmptyFilters } from '@texas/api/endpoints/search/searchPaTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

interface LeadTimeChartProps {
  data: ProductGroupLeadTime[];
}

export function LeadTimeChart({ data }: LeadTimeChartProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedData, setSelectedData] = useState<{
    productApprovalIds: number[];
    branchIds: number[];
    productGroupName: string;
    branchName: string;
  } | null>(null);

  const chartData = useMemo(() => {
    const allBranches = new Set<string>();
    const branchIdentifierMap = new Map<string, string>();

    data.forEach((pg) => {
      pg.branches.forEach((b) => {
        allBranches.add(b.branchName);
        branchIdentifierMap.set(b.branchName, b.branchIdentifier);
      });
    });

    const branchNames = Array.from(allBranches);
    const labels = data.map((pg) => pg.productGroupName);

    const datasets = branchNames.map((branchName) => {
      const branchIdentifier = branchIdentifierMap.get(
        branchName,
      ) as BranchIdentifier;
      const branchColor = getBranchColor(branchIdentifier);

      const branchData = data.map((pg) => {
        const branch = pg.branches.find((b) => b.branchName === branchName);
        return branch ? branch.averageDays : 0;
      });

      const cssVar = branchColor.match(/var\((--chakra-colors-[^)]+)\)/)?.[1];
      let backgroundColor = branchColor;
      let borderColor = branchColor;

      if (cssVar) {
        const computedColor = getComputedStyle(document.documentElement)
          .getPropertyValue(
            cssVar.replace('--chakra-colors-', '--chakra-colors-'),
          )
          .trim();

        if (computedColor) {
          backgroundColor = computedColor;
          borderColor = computedColor;
        }
      }

      return {
        label: branchName,
        data: branchData,
        backgroundColor,
        borderColor,
        borderWidth: 1,
      };
    });

    return { labels, datasets };
  }, [data]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event: any, elements: any[]) => {
        if (elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const dataIndex = element.index;

          const branchName = chartData.datasets[datasetIndex].label;
          const productGroupName = chartData.labels[dataIndex];

          const productGroup = data[dataIndex];
          const branch = productGroup.branches.find(
            (b) => b.branchName === branchName,
          );

          if (branch && branch.productApprovalIds.length > 0) {
            setSelectedData({
              productApprovalIds: branch.productApprovalIds,
              branchIds: [branch.branchId],
              productGroupName: String(productGroupName),
              branchName: branchName,
            });
            onOpen();
          }
        }
      },
      onHover: (event: any, activeElements: any[]) => {
        if (event.native?.target) {
          event.native.target.style.cursor =
            activeElements.length > 0 ? 'pointer' : 'default';
        }
      },
      scales: {
        x: {
          stacked: false,
          title: {
            display: true,
            text: t('widgets.paLeadTime.productGroup'),
          },
        },
        y: {
          stacked: false,
          title: {
            display: true,
            text: t('widgets.paLeadTime.avgLeadTime'),
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            font: {
              size: 11,
            },
          },
        },
        title: {
          display: true,
          text: t('widgets.paLeadTime.chartTitle'),
        },
        tooltip: {
          callbacks: {
            footer: () => {
              return String(t('widgets.paLeadTime.clickToViewDetails'));
            },
            label: (context: any) => {
              const branchName = context.dataset.label;
              const days = context.parsed.y.toFixed(1);

              const productGroup = data[context.dataIndex];
              const branch = productGroup.branches.find(
                (b) => b.branchName === branchName,
              );
              const count = branch?.count ?? 0;

              return t('widgets.paLeadTime.tooltipLabel', {
                branchName,
                days,
                count,
              });
            },
          },
        },
        annotation: {
          annotations: {
            maxLeadTimeLine: {
              type: 'line',
              yMin: 40,
              yMax: 40,
              borderColor: 'rgb(255, 99, 132)',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: t('widgets.paLeadTime.maxLeadTime'),
                position: 'end',
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                color: 'white',
                font: {
                  size: 10,
                },
              },
            },
          },
        },
      },
    }),
    [data, t, chartData, onOpen],
  );

  return (
    <>
      <Box height="400px" width="100%">
        <Bar data={chartData} options={options} />
      </Box>

      {selectedData && (
        <PaOverviewModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedData(null);
          }}
          filters={{
            ...defaultEmptyFilters,
            productApprovalIds: selectedData.productApprovalIds,
            branchIds: selectedData.branchIds,
          }}
          searchProps={{
            filters: [FilterOptionType.Branches],
            searchParams: {
              searchTerm: '',
              sortBy: '',
              sortDesc: false,
              page: 1,
              ...defaultEmptyFilters,
              productApprovalIds: selectedData.productApprovalIds,
              branchIds: selectedData.branchIds,
            },
          }}
          columns={PaTableColumns}
        />
      )}
    </>
  );
}
